import {
  haveSameRequirements,
  requirementKey,
} from 'helper/missionRequirementsMatch';
import {
  TOOL_PROVIDER_COMPANY,
  TOOL_PROVIDER_TEMP_WORKER,
} from './ToolProviderList';
import { toolProviderText } from './toolProviderTexts';

const isKnownProvider = (provider) =>
  provider === TOOL_PROVIDER_COMPANY || provider === TOOL_PROVIDER_TEMP_WORKER;

/**
 * The tools of a mission with their provider, as the api computes them
 * (`missionTools`), or the bare names of an older api. A provider that is
 * unknown or absent is the company, the default of a job description, so every
 * row always has a known provider.
 */
export const missionToolsOf = (mission) => {
  const rows = Array.isArray(mission?.missionTools)
    ? mission.missionTools
    : (Array.isArray(mission?.tools) ? mission.tools : []).map((name) => ({
        name,
      }));
  return rows
    .filter((row) => typeof row?.name === 'string' && row.name.trim() !== '')
    .map((row) => ({
      name: row.name,
      providedBy: isKnownProvider(row.providedBy)
        ? row.providedBy
        : TOOL_PROVIDER_COMPANY,
    }));
};

const findByKey = (rows, name) =>
  (rows || []).find((row) => requirementKey(row.name) === requirementKey(name));

/**
 * One row per checked tool. The rows keep the order of the form (the mission's
 * own at first), not the one of the checkboxes: reordering them on each change
 * would also reorder `mission.tools` on save. A tool checked since is appended,
 * with the mission's provider if it had one, else the company. The spelling of
 * the checked name wins.
 */
export const syncToolProviders = (checkedNames, currentRows, missionRows) => {
  const checked = new Map();
  (checkedNames || []).forEach((name) => {
    const key = requirementKey(name);
    if (!checked.has(key)) checked.set(key, name);
  });

  const kept = [];
  (currentRows || []).forEach((row) => {
    const key = requirementKey(row.name);
    if (!checked.has(key)) return;
    kept.push({ name: checked.get(key), providedBy: row.providedBy });
    checked.delete(key);
  });

  const added = [...checked.values()].map((name) => ({
    name,
    providedBy:
      findByKey(missionRows, name)?.providedBy ?? TOOL_PROVIDER_COMPANY,
  }));
  return [...kept, ...added];
};

const plainRows = (rows) =>
  (rows || []).map(({ name, providedBy }) => ({ name, providedBy }));

/**
 * The `toolProviders` change of the edit form, or null. It is due when a
 * provider was switched or when the list of tools changed; `after` then holds
 * every row, so the default « company » ones are saved too.
 */
export const toolProvidersChange = (missionRows, editedRows) => {
  const listChanged = !haveSameRequirements(
    (editedRows || []).map((row) => row.name),
    (missionRows || []).map((row) => row.name),
  );
  const providerSwitched = (editedRows || []).some((row) => {
    const original = findByKey(missionRows, row.name);
    return original && original.providedBy !== row.providedBy;
  });
  if (!listChanged && !providerSwitched) return null;
  return {
    field: 'toolProviders',
    before: plainRows(missionRows),
    after: plainRows(editedRows),
  };
};

/** « Gants fourni par l'entreprise, Casque apporté par l'intérimaire ». */
export const formatToolProviders = (rows) =>
  (Array.isArray(rows) ? rows : [])
    .map((row) =>
      isKnownProvider(row?.providedBy)
        ? `${row.name} ${toolProviderText(`verb.${row.providedBy}`)} ${toolProviderText(`who.${row.providedBy}`)}`
        : row?.name,
    )
    .filter(Boolean)
    .join(', ');
